import PropTypes from 'prop-types';

import { loadReCaptcha } from 'react-recaptcha-v3';

import React from 'react';
import has from 'lodash/has';
import omit from 'lodash/omit';
import set from 'lodash/set';
import throttle from 'lodash/throttle';
import classNames from 'classnames';

import { NotificationBar } from '@eb/eds-notification';
import { withMainControls } from '@eb/eds-structure';

import { ICON_TYPE_PROP_TYPE } from '@eb/eds-icon';
import { TYPE_ERROR } from '@eb/eds-notification';

import PageHeader from '../../components/PageHeader';
import LookUpForm from '../../components/forms/LookUpForm';
import CheckoutSocialLoginForm from '../../components/forms/CheckoutSocialLoginForm';
import LogInForm from '../../components/forms/LogInForm';

import {
    BASE_URL,
    CHECKOUT_PATH,
    LOG_IN_PATH,
    SOCIAL_LOG_IN_PATH,
} from '../../constants';
import { PAGE_HEADER_INFO_MAP_CHECKOUT } from '../../constants/messages';
import { normalizePath } from '../../utils';
import { RESIZE_THROTTLE_DELAY } from './constants';

import './checkoutSignInScreen.scss';

import { AlertChunky } from '@eb/eds-iconography';

const FORM_MAP = {
    [CHECKOUT_PATH]: LookUpForm,
    [LOG_IN_PATH]: LogInForm,
    [SOCIAL_LOG_IN_PATH]: CheckoutSocialLoginForm,
};

export class CheckoutSignInPage extends React.PureComponent {
    static propTypes = {
        currentFormType: PropTypes.string,
        onLookupUserForCheckout: PropTypes.func,
        onLogInUser: PropTypes.func,
        onSocialLogInUser: PropTypes.func,
        onTermsOfServiceClick: PropTypes.func,
        onComunityGuidelinesClick: PropTypes.func,
        onPrivacyPolicyClick: PropTypes.func,
        onCheckoutSignInHeightChange: PropTypes.func,
        email: PropTypes.string,
        moveBack: PropTypes.func,
        trackEmailMismatchShowed: PropTypes.func,
        clearAuthNotification: PropTypes.func,
        recaptcha2PublicKey: PropTypes.string,
        recaptcha3PublicKey: PropTypes.string,
        showCaptcha: PropTypes.bool,
        fp: PropTypes.bool,
        onSocialProviderClick: PropTypes.func,
        socialSSOSettings: PropTypes.shape({
            facebook: PropTypes.object,
        }),
        authNotificationOptions: PropTypes.shape({
            message: PropTypes.node,
            type: PropTypes.string,
            iconType: ICON_TYPE_PROP_TYPE,
        }),
        shouldRenderAuthNotification: PropTypes.bool,
        userHasSoftAccount: PropTypes.bool,
    };

    state = {
        shouldAnimate: false,
        pageHeight: 0,
    };

    constructor(props) {
        super(props);

        this.checkoutSignInRef = React.createRef();
        this._handleWindowResize = throttle(
            this._handleWindowResize.bind(this),
            RESIZE_THROTTLE_DELAY,
            { leading: false },
        );
    }

    componentDidMount() {
        const {
            router: {
                location: { query },
            },
        } = this.props;
        const { pageHeight } = this.state;

        if (!this.props.disableRecaptcha3) {
            loadReCaptcha(this.props.recaptcha3PublicKey);
        }

        this._handlePageHeight(pageHeight);

        window.addEventListener('resize', this._handleWindowResize);
    }

    componentWillReceiveProps({ location: { pathname } }) {
        const { route } = this.props;

        //Needed so that the submit button correctly animates up when the
        //user navigates to the /signin, but not on initial load.
        if (pathname) {
            //In certain cases the pathname is /signin/ rather than /signin
            //due to this we want to normalize the path to /signin
            const normalizedPath = normalizePath(pathname);

            //This conditional checks that the route we are going TO is the base page,
            //but the route we are currently on is a nested route (/login or /signup etc)
            if (normalizedPath === BASE_URL && has(route, 'path')) {
                this.setState({ shouldAnimate: true });
            }
        }
    }

    componentDidUpdate(prevProps, prevState) {
        this._handlePageHeight(prevState.pageHeight);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this._handleWindowResize);
    }

    _handlePageHeight(prevPageHeight) {
        const currentPageHeight = this._getPageHeight();

        if (currentPageHeight !== prevPageHeight) {
            this._setPageHeight(currentPageHeight);
        }
    }

    _handleWindowResize() {
        const { pageHeight } = this.state;

        this._handlePageHeight(pageHeight);
    }

    _getPageHeight() {
        const checkoutSignIn = this.checkoutSignInRef.current;

        return parseInt(checkoutSignIn.offsetHeight);
    }

    _setPageHeight(pageHeight) {
        const { onCheckoutSignInHeightChange } = this.props;

        this.setState({
            pageHeight,
        });

        onCheckoutSignInHeightChange(pageHeight);
    }

    _getSubmitAction() {
        const { onLookupUserForCheckout, onLogInUser } = this.props;
        const formType = this._getFormType();

        const actionMap = {
            [LOG_IN_PATH]: onLogInUser,
            [CHECKOUT_PATH]: onLookupUserForCheckout,
        };

        return actionMap[formType];
    }

    _handleSubmit = (formData) => {
        const { clearAuthNotification } = this.props;
        const action = this._getSubmitAction();
        const formType = this._getFormType();

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        return action(formData);
    };

    _handleOnSocialLogIn = (socialProvider, socialResponse) => {
        const {
            clearAuthNotification,
            onSocialLogInUser,
            socialSSOSettings,
        } = this.props;

        // clear notification everytime form is submitted
        if (clearAuthNotification) {
            clearAuthNotification();
        }

        // add param to call api, depending on feature flag
        let skipCreation = false;

        if (socialSSOSettings) {
            skipCreation = socialSSOSettings.twoStepsSignup || false;
        }

        if (onSocialLogInUser) {
            onSocialLogInUser(socialProvider, {
                skipCreation,
                ...socialResponse,
            });
        }
    };

    _handleOnSocialProviderClick = (socialProvider, stateFromProvider) => {
        const { onSocialProviderClick } = this.props;

        if (onSocialProviderClick) {
            onSocialProviderClick({
                socialProvider,
                stateFromProvider,
            });
        }
    };

    _handleOnGetStartedClick = () => {
        const { onGetStartedClick } = this.props;

        if (onGetStartedClick) {
            onGetStartedClick();
        }
    };

    _handleTermsOfServiceClick = () => {
        const { onTermsOfServiceClick } = this.props;

        if (onTermsOfServiceClick) {
            onTermsOfServiceClick();
        }
    };

    _handleComunityGuidelinesClick = () => {
        const { onComunityGuidelinesClick } = this.props;

        if (onComunityGuidelinesClick) {
            onComunityGuidelinesClick();
        }
    };

    _handlePrivacyPolicyClick = () => {
        const { onPrivacyPolicyClick } = this.props;

        if (onPrivacyPolicyClick) {
            onPrivacyPolicyClick();
        }
    };

    _getFormType() {
        const formType = CHECKOUT_PATH;
        const { currentFormType } = this.props;

        return currentFormType || formType;
    }

    _handleShowNotification(error) {
        const { addMainNotification } = this.props;

        /*Limit the call to addMainNotification to only if
        a notification hasn't been shown since last submit.*/
        if (!this._hasNotification) {
            addMainNotification({
                type: TYPE_ERROR,
                children: error,
                iconType: <AlertChunky />,
            });

            this._hasNotification = true;
        }
    }

    _renderAuthNotification = () => {
        const {
            authNotificationOptions,
            shouldRenderAuthNotification,
        } = this.props;

        let component = null;

        if (shouldRenderAuthNotification) {
            const { message, ...props } = authNotificationOptions;

            component = (
                <div className="eds-l-mar-top-4 eds-l-mar-bot-8">
                    <NotificationBar {...props}>{message}</NotificationBar>
                </div>
            );
        }

        return component;
    };

    _handleClearNotification() {
        const { hideMainNotification } = this.props;

        this._hasNotification = false;
        hideMainNotification && hideMainNotification();
    }

    _handleOnSocialButtonLoadComplete() {
        const pageHeight = this._getPageHeight();
        this._setPageHeight(pageHeight);
    }

    render() {
        const {
            recaptcha2PublicKey,
            recaptcha3PublicKey,
            showCaptcha,
            email,
            moveBack,
            fp,
            socialSSOSettings,
            shouldRenderAuthNotification,
            disableRecaptcha3,
            onFBAccountNotConnected,
            socialAuthenticationData,
            clearAuthNotification,
            userHasSoftAccount,
        } = this.props;

        const { shouldAnimate } = this.state;
        const formType = this._getFormType();
        const DynamicForm = FORM_MAP[formType];
        const mapHeaderInfo = PAGE_HEADER_INFO_MAP_CHECKOUT;
        const formDisqualifications = [userHasSoftAccount];
        const shouldHideForm = formDisqualifications.includes(true);
        let creatorEncouragementFooter;
        let syncErrors;

        let { buttonCTA, ...pageHeaderProps } = mapHeaderInfo[
            `${formType}Form`
        ];

        pageHeaderProps = set(pageHeaderProps);

        // hide subtitle when an auth notification is being displayed
        if (shouldRenderAuthNotification) {
            pageHeaderProps = omit(pageHeaderProps, 'subtitleText');
        }

        const gridClasses = classNames(
            'eds-g-cell',
            'container-max-width',
            'eds-g-cell--has-overflow',
        );

        const dynamicForm = (
            <DynamicForm
                formErrors={syncErrors}
                buttonCTA={buttonCTA}
                formType={formType}
                hideSocialLogin={true}
                isCheckoutAuthenticationFlow={true}
                onFBAccountNotConnected={onFBAccountNotConnected}
                onSubmit={this._handleSubmit}
                onSocialConnectVerified={this._handleOnSocialLogIn}
                onSocialProviderClick={this._handleOnSocialProviderClick}
                onGetStartedClick={this._handleOnGetStartedClick}
                onTermsOfServiceClick={this._handleTermsOfServiceClick}
                onComunityGuidelinesClick={this._handleComunityGuidelinesClick}
                onPrivacyPolicyClick={this._handlePrivacyPolicyClick}
                clearAuthNotification={clearAuthNotification}
                handleEmailEdit={moveBack}
                recaptcha2PublicKey={recaptcha2PublicKey}
                recaptcha3PublicKey={recaptcha3PublicKey}
                disableRecaptcha3={disableRecaptcha3}
                showCaptcha={showCaptcha}
                email={email}
                fp={fp}
                showNotification={this._handleShowNotification.bind(this)}
                clearNotification={this._handleClearNotification.bind(this)}
                shouldAnimate={shouldAnimate}
                socialSSOSettings={socialSSOSettings}
                socialAuthenticationData={socialAuthenticationData}
                onSocialButtonLoadComplete={this._handleOnSocialButtonLoadComplete.bind(
                    this,
                )}
            />
        );

        return (
            <div ref={this.checkoutSignInRef} className="eds-l-pad-vert-6">
                <div className="eds-g-group eds-align--center">
                    <div className={gridClasses}>
                        <PageHeader {...pageHeaderProps} />

                        {this._renderAuthNotification()}

                        {shouldHideForm ? null : dynamicForm}

                        {creatorEncouragementFooter}
                    </div>
                </div>
            </div>
        );
    }
}

export default withMainControls(CheckoutSignInPage);
